import {
  Box,
  Card,
  FileUpload,
  Grid,
  GridItem,
  HStack,
  Heading,
  IconButton,
  Input,
  Link,
  Spacer,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import * as React from "react";
import { FaShopify } from "react-icons/fa6";
import { LuDownload, LuFacebook, LuList, LuUpload } from "react-icons/lu";
import { useParams } from "react-router-dom";

import {
  connectIntegration,
  disconnectIntegration,
  getFacebookPageFeed,
  getFacebookPages,
} from "../../api/integration";
import { uploadProductMargins } from "../../api/workspace";
import { Button } from "../../components/button";
import {
  DialogActionTrigger,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTrigger,
} from "../../components/dialog";
import { EmptyState } from "../../components/empty-state";
import { Field } from "../../components/field";
import PageBody from "../../components/page-body";
import {
  ProgressCircleRing,
  ProgressCircleRoot,
} from "../../components/progress-circle";
import SectionHeader from "../../components/section-header";
import {
  useAppDispatch,
  useAppSelector,
  useLoading,
  useToaster,
} from "../../hooks";
import { loadIntegrations } from "../../store/workspaceSlice";
import { FacebookPage, FacebookPageFeedItem } from "../../types/facebook";
import {
  IntegrationContextField,
  IntegrationContextFieldType,
  IntegrationSchema,
  IntegrationStatus,
  IntegrationStyle,
} from "../../types/integration";

export default function IntegrationsPage() {
  const dispatch = useAppDispatch();
  const { integrations, slug } = useAppSelector((s) => s.workspace);
  const withToast = useToaster();
  const connect = React.useCallback(
    async (integration: IntegrationSchema, context: Record<string, any>) => {
      await withToast(async () => {
        switch (integration.style) {
          case IntegrationStyle.custom:
            {
              await connectIntegration(slug, integration.id, context);
              document.location.reload();
            }
            break;
          case IntegrationStyle.oauth:
            {
              const { "oauth-url": oauthURL } = await connectIntegration(
                slug,
                integration.id,
                context,
              );
              if (oauthURL === undefined) {
                document.location.reload();
              } else {
                document.location.href = oauthURL;
              }
            }
            break;
        }
      });
    },
    [slug, withToast],
  );
  const disconnect = React.useCallback(
    async (integration: IntegrationSchema) => {
      await disconnectIntegration(slug, integration.id);
      await dispatch(loadIntegrations()).unwrap();
    },
    [dispatch, slug],
  );
  React.useEffect(() => {
    dispatch(loadIntegrations());
  }, [dispatch]);

  const [isUploadingMargins, wrapIsUploadingMargins] = useLoading(false);
  const uploadMargins = React.useCallback(
    async ({ files }: { files: File[] }) => {
      await wrapIsUploadingMargins(async () => {
        await withToast(async () => {
          if (files.length !== 1) return;
          await uploadProductMargins(slug, files[0]);
        });
      });
    },
    [slug, withToast, wrapIsUploadingMargins],
  );

  return (
    <PageBody>
      <Stack gap="5">
        <Stack>
          <SectionHeader
            title="Integrations"
            description="Manage your integrations."
          />
          <Grid gap="2" templateColumns="repeat(2, 1fr)" maxW="xl">
            {integrations.map((schema) => (
              <GridItem key={schema.id}>
                <IntegrationCard
                  {...schema}
                  onConnect={async (context) => await connect(schema, context)}
                  onDisconnect={async () => await disconnect(schema)}
                />
              </GridItem>
            ))}
          </Grid>
        </Stack>
        <Stack>
          <SectionHeader
            title="Products"
            description="Manage your profit margins."
          />
          <HStack maxW="md">
            <Button colorPalette="gray" asChild>
              <Link href={`/api/v1/workspaces/${slug}/products.csv`}>
                <LuDownload /> Download Products
              </Link>
            </Button>

            <FileUpload.Root accept={["text/csv"]} onFileAccept={uploadMargins}>
              <FileUpload.HiddenInput />
              <FileUpload.Trigger asChild>
                <Button colorPalette="gray" loading={isUploadingMargins}>
                  <LuUpload /> Upload Product Margins
                </Button>
              </FileUpload.Trigger>
            </FileUpload.Root>
          </HStack>
          <HStack maxW="xl">
            <Text color="fg.muted">
              <strong>Instructions:</strong> First, download your list of
              products. Then, update the cost columns of each product. Finally,
              upload the updated file.
            </Text>
          </HStack>
        </Stack>
      </Stack>
    </PageBody>
  );
}

interface IntegrationCardProps extends IntegrationSchema {
  onDisconnect: () => Promise<void>;
  onConnect: (context: Record<string, any>) => Promise<void>;
}

function IntegrationCard({
  context,
  id,
  label,
  onConnect,
  onDisconnect,
  status,
}: IntegrationCardProps) {
  const { workspace } = useParams();
  const [isLoading, wrapLoading] = useLoading();
  const connect = React.useCallback(
    async (context?: Record<string, any>) => {
      await wrapLoading(async () => {
        await onConnect(context ?? {});
      });
    },
    [onConnect, wrapLoading],
  );
  const disconnect = React.useCallback(async () => {
    await wrapLoading(async () => {
      await onDisconnect();
    });
  }, [onDisconnect, wrapLoading]);

  let action: React.ReactNode;
  switch (status) {
    case IntegrationStatus.connected:
      {
        action = (
          <Button
            w="full"
            onClick={disconnect}
            loading={isLoading}
            colorPalette="red"
          >
            Disconnect
          </Button>
        );

        if (id === "facebook" && workspace === "meta-app-review") {
          action = (
            <HStack>
              <FacebookPagesDialog />
              {action}
            </HStack>
          );
        }
      }
      break;
    default: {
      action = (
        <Button w="full" onClick={() => connect()} loading={isLoading}>
          Connect
        </Button>
      );

      if (context.length > 0) {
        action = (
          <ConnectContextDialog
            connect={connect}
            id={id}
            label={label}
            isLoading={isLoading}
            schema={context}
          />
        );
      }
    }
  }

  let icon: React.ReactNode;
  switch (id) {
    case "facebook":
    case "facebook-capi":
      icon = <LuFacebook size="24px" />;
      break;
    case "shopify":
      icon = <FaShopify size="24px" />;
      break;
  }

  return (
    <Card.Root h={"160px" as any} variant="elevated">
      <Card.Body>
        <HStack>
          <Box bg="bg.muted" borderRadius="full" p="2">
            {icon}
          </Box>
          <Heading color="fg" size="sm">
            {label}
          </Heading>
        </HStack>
      </Card.Body>
      <Card.Footer>{action}</Card.Footer>
    </Card.Root>
  );
}

interface ConnectContextDialogProps {
  connect: (context: Record<string, any>) => Promise<void>;
  id: string;
  label: string;
  isLoading: boolean;
  schema: IntegrationContextField[];
}

function ConnectContextDialog(props: ConnectContextDialogProps) {
  const [context, setContext] = React.useState<Record<string, any>>({});
  return (
    <DialogRoot>
      <DialogTrigger asChild>
        <Button w="full">Connect</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>Connect {props.label}</DialogHeader>
        <DialogBody>
          <Stack gap="3">
            {props.schema.map((field) => (
              <ConnectContextField
                {...field}
                key={field.id}
                onChange={(value) =>
                  setContext({ ...context, [field.id]: value })
                }
              />
            ))}
          </Stack>
        </DialogBody>
        <DialogFooter>
          <DialogActionTrigger asChild>
            <Button variant="outline">Cancel</Button>
          </DialogActionTrigger>
          <Button
            loading={props.isLoading}
            onClick={() => props.connect(context)}
          >
            Connect
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
}

interface ConnectContextFieldProps extends IntegrationContextField {
  onChange: (value: string) => void;
}

function ConnectContextField({
  label,
  onChange,
  type,
}: ConnectContextFieldProps) {
  switch (type) {
    case IntegrationContextFieldType.AccessToken:
    case IntegrationContextFieldType.FacebookCAPIDataset:
      return (
        <Field label={label}>
          <Input required={true} onChange={(e) => onChange(e.target.value)} />
        </Field>
      );

    case IntegrationContextFieldType.ShopifyAccessToken:
      return (
        <Field label={label}>
          <Input required={true} onChange={(e) => onChange(e.target.value)} />
        </Field>
      );

    case IntegrationContextFieldType.ShopifyShopURL:
      return (
        <Field label={label}>
          <HStack>
            {"https://"}
            <Input required={true} onChange={(e) => onChange(e.target.value)} />
            {".myshopify.com"}
          </HStack>
        </Field>
      );
  }
}

function FacebookPagesDialog() {
  const { workspace } = useParams();
  const [loading, wrapLoading] = useLoading();
  const [pages, setPages] = React.useState<FacebookPage[]>([]);
  React.useEffect(() => {
    wrapLoading(async () => {
      setPages(await getFacebookPages(workspace));
    });
  }, [workspace, wrapLoading]);

  return (
    <DialogRoot>
      <DialogTrigger asChild>
        <IconButton variant="subtle">
          <LuList />
        </IconButton>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>Facebook Pages</DialogHeader>
        <DialogBody>
          {loading ? (
            <ProgressCircleRoot>
              <ProgressCircleRing />
            </ProgressCircleRoot>
          ) : pages.length === 0 ? (
            <EmptyState
              icon={<LuList />}
              title="No Pages"
              description="You don't have any Facebook pages."
            />
          ) : (
            <VStack align="left">
              {pages.map((p) => (
                <HStack
                  key={p.id}
                  borderRadius="sm"
                  p="2"
                  _hover={{ background: "bg.muted" }}
                >
                  <Text>{p.name}</Text>
                  <Spacer />
                  <FacebookPageFeedDialog page={p} />
                </HStack>
              ))}
            </VStack>
          )}
        </DialogBody>
      </DialogContent>
    </DialogRoot>
  );
}

interface FacebookPageFeedDialogProps {
  page: FacebookPage;
}

function FacebookPageFeedDialog({ page }: FacebookPageFeedDialogProps) {
  const [loading, wrapLoading] = useLoading();
  const [items, setItems] = React.useState<FacebookPageFeedItem[]>([]);
  React.useEffect(() => {
    wrapLoading(async () => {
      setItems(await getFacebookPageFeed(page));
    });
  }, [page, wrapLoading]);

  return (
    <DialogRoot>
      <DialogTrigger asChild>
        <IconButton variant="subtle">
          <LuList />
        </IconButton>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>{page.name}</DialogHeader>
        <DialogBody>
          {loading ? (
            <ProgressCircleRoot>
              <ProgressCircleRing />
            </ProgressCircleRoot>
          ) : items.length === 0 ? (
            <EmptyState
              icon={<LuList />}
              title="No Feed Items"
              description="This page has no feed items."
            />
          ) : (
            <VStack align="left">
              {items.map((i, idx) => (
                <HStack key={idx} p="2">
                  <Text>{i.message}</Text>
                </HStack>
              ))}
            </VStack>
          )}
        </DialogBody>
      </DialogContent>
    </DialogRoot>
  );
}
